import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconCheck } from "@tabler/icons-react";

import { Text } from "@fragments";

import { getVisibilityOptions } from "../../../enums/visibilityOptions";
import OptionPopper from "../OptionPopper";

/**
 * "Who can see" panel: one row per audience, the current one checked. Picking
 * a row applies it and closes the panel.
 */
export default function VisibilityPopper({ open, anchorEl, value, onChange, onClose }) {
    const { t } = useTranslation("groupModals");

    const options = useMemo(() => getVisibilityOptions(t), [t]);

    function select(optionValue) {
        onChange(optionValue);
        onClose();
    }

    return (
        <OptionPopper
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            width={300}
            label={t("publishPost.visibility.title")}
        >
            <div className="pp-popper__header">
                <Text.subheading contrast={6}>{t("publishPost.visibility.title")}</Text.subheading>
            </div>

            <div className="pp-popper__list" role="radiogroup" aria-label={t("publishPost.visibility.title")}>
                {options.map(({ value: optionValue, Icon, label, description }) => (
                    <OptionRow
                        key={optionValue}
                        Icon={Icon}
                        label={label}
                        description={description}
                        selected={optionValue === value}
                        onSelect={() => select(optionValue)}
                    />
                ))}
            </div>
        </OptionPopper>
    );
}

export function OptionRow({ Icon, label, description, selected, onSelect }) {
    // Enter/Space picks; the arrows walk the rows of this panel's group.
    function handleKeyDown(event) {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect();
            return;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            const rows = Array.from(
                event.currentTarget.closest('[role="radiogroup"]')?.querySelectorAll('[role="radio"]') || []
            );
            const index = rows.indexOf(event.currentTarget);
            const step = event.key === "ArrowDown" ? 1 : -1;
            rows[(index + step + rows.length) % rows.length]?.focus();
        }
    }

    return (
        <div
            role="radio"
            aria-checked={selected}
            tabIndex={0}
            className={`pp-option${selected ? " pp-option--active" : ""}`}
            onClick={onSelect}
            onKeyDown={handleKeyDown}
        >
            <span className="pp-option__media" aria-hidden="true">
                {Icon && <Icon />}
            </span>

            <div className="pp-option__text">
                <Text.label contrast={selected ? 10 : 8}>{label}</Text.label>
                {description && <Text.caption contrast={6}>{description}</Text.caption>}
            </div>

            <span className="pp-option__check" aria-hidden="true">
                {selected && <IconCheck />}
            </span>
        </div>
    );
}
